<template>
  <div ref="host" class="inline-flex" @pointerdown="emit('poke')" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { claimStage } from "../../composables/use-fency";
import {
  Fency,
  type FencyCue,
  type FencyMood,
  type FencyPose,
} from "../../shared/fency/fency";

// Vue shell around the shared Fency engine (src/shared/fency, the same one the
// browser extension uses), which does all the animating. Same props as the
// extension's FencyCharacter, plus the engine options it leaves at defaults.
const props = withDefaults(
  defineProps<{
    pose?: FencyPose;
    height?: number;
    poses?: FencyPose[];
    fit?: "body" | "all";
    mood?: FencyMood;
    cues?: FencyCue[];
    // Fly up into place on mount.
    entrance?: boolean;
    // Eyes follow the pointer; a click makes Fency hop.
    interactive?: boolean;
    // Where the eyes should look (-1..1 each way); null lets Fency choose.
    look?: { x: number; y: number } | null;
    // A Fency of the page: the shell's companion steps aside while it's
    // shown (one Fency per screen). Off for the companion itself.
    stage?: boolean;
  }>(),
  {
    pose: "thumbs",
    height: 128,
    poses: undefined,
    fit: "body",
    mood: "idle",
    cues: () => [],
    entrance: true,
    interactive: true,
    look: null,
    stage: true,
  },
);

// A click or tap on Fency (the engine answers it with a happy hop).
const emit = defineEmits<{ (e: "poke"): void }>();

const host = ref<HTMLElement | null>(null);
let fency: Fency | null = null;
let releaseStage: (() => void) | null = null;

onMounted(() => {
  if (props.stage) releaseStage = claimStage();
  fency = new Fency({
    pose: props.pose,
    height: props.height,
    poses: props.poses,
    fit: props.fit,
    cues: props.cues,
    entrance: props.entrance,
    interactive: props.interactive,
  });
  fency.setMood(props.mood);
  fency.lookAt(props.look);
  host.value?.appendChild(fency.element);
});

watch(
  () => props.pose,
  (pose) => fency?.setPose(pose),
);
watch(
  () => props.mood,
  (mood) => fency?.setMood(mood),
);
watch(
  () => props.look,
  (look) => fency?.lookAt(look),
);

onBeforeUnmount(() => {
  fency?.destroy();
  releaseStage?.();
});

defineExpose({ hop: () => fency?.hop() });
</script>
